import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Constants } from '@shared';
import { Logo } from '@resources';

interface CommunityLogoProps extends React.HTMLAttributes<HTMLDivElement> {
    className?: string;
    onClick?: (e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => void;
    communityShortName?: string; // Used by Storybook
}

function CommunityLogo({ className, onClick, communityShortName, ...otherProps }: CommunityLogoProps) {

    const domainToUrl = (shortName?: string) => {
        if (!shortName) {
            const { hostname } = window.location;
            shortName = hostname === `lvh.me` ? `localhost` : hostname.split(`.`)[0];
        }
        return `${Constants.IMG_BUCKET}/communities/192x128/${shortName}.png`;
    };

    const [imageUrl, setImageUrl] = useState(domainToUrl(communityShortName));
    useEffect(() => {
        // Watch out for changing subdomain...
        setImageUrl(domainToUrl(communityShortName));
    }, [communityShortName, window.location.hostname]);

    return (
        <div
            {...otherProps}
            onClick={onClick}
            {...(onClick && { tabIndex: 0 })}
            className={classNames(
                `flex items-end focus-visible:outline-focus-ring rounded gap-2`,
                { 'cursor-pointer': onClick },
                className
            )}
            {...(onClick && { role: `button` })}
            {...(onClick && {
                onKeyDown: (e) => {
                    if (e.key === `Enter` || e.key === ` `) {
                        e.preventDefault();
                        onClick?.(e);
                    }
                }
            })}
        >
            <div className="w-[60px] h-10 md:w-[96px] md:h-16 flex items-center justify-center">
                <img src={imageUrl} className="max-w-full max-h-full object-contain" alt="Community logo" />
            </div>
            <div className="flex flex-col items-start gap-1" role="img" aria-label="Powered by Sunbound">
                <span className="text-neutral-500 text-[10px] leading-[12px]" aria-hidden="true">Powered by</span>
                <Logo className="h-3 md:h-4 w-auto text-neutral-500" aria-hidden="true" />
            </div>
        </div>
    );
}

export default CommunityLogo;
